/**
 * Analytics React Query Hooks
 * Provides cached data fetching for user behavior analytics
 */

import { useQuery } from '@tanstack/react-query';
import {
  getAnalyticsDashboard,
  getEventCounts,
  getFeatureUsage,
  getScreenViews,
  getUserJourneyFunnel,
  getRetentionRates,
  getAnalyticsHealth,
  type AnalyticsDashboard,
  type EventCount,
  type FeatureUsageStats,
  type ScreenViewStats,
  type UserJourneyStep,
  type AnalyticsHealthStatus,
} from '@/lib/api/analytics';
import { DEFAULT_EXCLUDE_TEAM } from '@/lib/analytics/teamFilter';

interface UseAnalyticsOptions {
  startDate?: string;
  endDate?: string;
  excludeTeam?: boolean;
  enabled?: boolean;
}

/**
 * Hook for fetching complete analytics dashboard
 */
export function useAnalyticsDashboard(options: UseAnalyticsOptions = {}) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery<AnalyticsDashboard, Error>({
    queryKey: ['analytics', 'dashboard', startDate, endDate, excludeTeam],
    queryFn: () => getAnalyticsDashboard({ startDate, endDate, excludeTeam }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching event counts
 */
export function useEventCounts(
  options: UseAnalyticsOptions & { events?: string[] } = {}
) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, events, enabled = true } = options;

  return useQuery<EventCount[], Error>({
    queryKey: ['analytics', 'events', startDate, endDate, excludeTeam, events],
    queryFn: () => getEventCounts({ startDate, endDate, excludeTeam, events }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching feature usage
 */
export function useFeatureUsage(options: UseAnalyticsOptions = {}) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery<FeatureUsageStats[], Error>({
    queryKey: ['analytics', 'features', startDate, endDate, excludeTeam],
    queryFn: () => getFeatureUsage({ startDate, endDate, excludeTeam }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching screen views
 */
export function useScreenViews(options: UseAnalyticsOptions = {}) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery<ScreenViewStats[], Error>({
    queryKey: ['analytics', 'screens', startDate, endDate, excludeTeam],
    queryFn: () => getScreenViews({ startDate, endDate, excludeTeam }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching user journey funnel
 */
export function useUserJourneyFunnel(options: UseAnalyticsOptions = {}) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery<UserJourneyStep[], Error>({
    queryKey: ['analytics', 'funnel', startDate, endDate, excludeTeam],
    queryFn: () => getUserJourneyFunnel({ startDate, endDate, excludeTeam }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching retention rates
 */
export function useRetentionRates(options: UseAnalyticsOptions = {}) {
  const { startDate, endDate, excludeTeam = DEFAULT_EXCLUDE_TEAM, enabled = true } = options;

  return useQuery<Record<string, number>, Error>({
    queryKey: ['analytics', 'retention', startDate, endDate, excludeTeam],
    queryFn: () => getRetentionRates({ startDate, endDate, excludeTeam }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for checking analytics health status
 */
export function useAnalyticsHealth() {
  return useQuery<AnalyticsHealthStatus, Error>({
    queryKey: ['analytics', 'health'],
    queryFn: getAnalyticsHealth,
    staleTime: 60 * 1000, // 1 minute
    retry: 1,
  });
}
